import { useEffect, useRef, useState } from "react";
import type { AdminProject } from "../../api/admin";
import { useDialogFocus } from "./useDialogFocus";
import { Avatar, Meter, ProjectGlyphTile, StatusPill } from "./MemberParts";
import { IconMail, IconShield, IconTrash, IconUser, IconX } from "./icons";
import {
  KIND_LABEL,
  joinedLabel,
  relativeTime,
  usd,
  type MemberVM,
} from "./memberModel";
import type { SheetFocus } from "./types";

type Props = {
  m: MemberVM;
  projects: AdminProject[];
  /** Assigned project ids, or null while loading. */
  assignedIds: string[] | null;
  initialFocus?: SheetFocus;
  onClose: () => void;
  onSetRole: (id: number, role: "admin" | "member") => void;
  onSetMonthly: (id: number, dollars: number | null) => void;
  onSetDaily: (id: number, dollars: number | null) => void;
  onToggleProject: (id: number, projectId: string, next: boolean) => void;
  onRemove: (id: number) => void;
};

/** Whole-dollar when even, two-decimals otherwise; blank when unlimited. */
function fmtCap(cents: number | null): string {
  if (cents == null) return "";
  const d = cents / 100;
  return d % 1 === 0 ? d.toFixed(0) : d.toFixed(2);
}

function CapInput({
  label,
  per,
  cents,
  onSave,
}: {
  label: string;
  per: string;
  cents: number | null;
  onSave: (dollars: number | null) => void;
}) {
  const [draft, setDraft] = useState(() => fmtCap(cents));
  useEffect(() => {
    setDraft(fmtCap(cents));
  }, [cents]);

  const commit = () => {
    const t = draft.trim();
    if (t === "") {
      onSave(null);
      return;
    }
    const n = Number(t);
    if (!Number.isFinite(n) || n < 0) {
      setDraft(fmtCap(cents));
      return;
    }
    onSave(n);
  };

  return (
    <label className="cap-field">
      <span className="cap-label">{label}</span>
      <div className="budget-input">
        <span className="cur">$</span>
        <input
          value={draft}
          placeholder="∞"
          inputMode="decimal"
          aria-label={`${label} (blank = unlimited)`}
          onChange={(e) => setDraft(e.target.value.replace(/[^0-9.]/g, ""))}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
            if (e.key === "Escape") {
              setDraft(fmtCap(cents));
              e.currentTarget.blur();
            }
          }}
        />
        <span className="per">/ {per}</span>
      </div>
    </label>
  );
}

/**
 * Right-hand slide-over that holds every per-member control: role, daily +
 * monthly caps, project access toggles, an activity summary, and removal.
 */
export function MemberSheet({
  m,
  projects,
  assignedIds,
  initialFocus,
  onClose,
  onSetRole,
  onSetMonthly,
  onSetDaily,
  onToggleProject,
  onRemove,
}: Props) {
  const sheetRef = useRef<HTMLElement>(null);
  const budgetRef = useRef<HTMLDivElement>(null);
  const accessRef = useRef<HTMLDivElement>(null);

  // Move focus into the panel, trap Tab, close on Escape, restore focus on close.
  useDialogFocus(sheetRef, onClose);

  // Jump to the section the caller asked for (kebab "Set budget" / "Access").
  useEffect(() => {
    const target = initialFocus === "access" ? accessRef.current : initialFocus === "budget" ? budgetRef.current : null;
    target?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [initialFocus]);

  const cannotDemoteSelf = m.you && m.role === "admin";
  const assignedCount = assignedIds ? assignedIds.length : 0;

  return (
    <>
      <div className="sheet-backdrop" onClick={onClose} aria-hidden="true" />
      <aside ref={sheetRef} className="sheet" role="dialog" aria-modal="true" aria-label={`Manage ${m.name}`}>
        <div className="sheet-head">
          <Avatar m={m} />
          <div className="sheet-id">
            <h3>
              {m.name}
              {m.you && <span className="you-tag">you</span>}
            </h3>
            <div className="uident-email">{m.email}</div>
          </div>
          <button className="sheet-close" onClick={onClose} aria-label="Close">
            <IconX />
          </button>
        </div>

        <div className="sheet-body">
          {m.status === "pending" && (
            <div className="sheet-sec">
              <div className="invite-banner">
                <IconMail /> Account created · awaiting first login &amp; 2FA setup
              </div>
            </div>
          )}

          {/* role */}
          <div className="sheet-sec">
            <div className="sheet-sec-h">Role</div>
            <div className="role-seg">
              <button
                className={`admin${m.role === "admin" ? " on" : ""}`}
                onClick={() => onSetRole(m.id, "admin")}
              >
                <IconShield /> Admin
              </button>
              <button
                className={`member${m.role === "member" ? " on" : ""}`}
                disabled={cannotDemoteSelf}
                title={cannotDemoteSelf ? "You cannot demote your own admin account" : undefined}
                onClick={() => !cannotDemoteSelf && onSetRole(m.id, "member")}
              >
                <IconUser /> Member
              </button>
            </div>
            <p className="role-hint">
              {m.role === "admin"
                ? "Admins manage members, set budgets, and reach every project."
                : "Members work in the projects they are granted and spend within their own cap."}
            </p>
          </div>

          {/* budgets */}
          <div className="sheet-sec" ref={budgetRef}>
            <div className="sheet-sec-h">Token budget</div>
            <div className="cap-row">
              <CapInput label="Monthly cap" per="mo" cents={m.monthlyCapCents} onSave={(d) => onSetMonthly(m.id, d)} />
              <CapInput label="Daily cap" per="day" cents={m.dailyCapCents} onSave={(d) => onSetDaily(m.id, d)} />
            </div>
            {m.status !== "pending" && (
              <div className="budget-now">
                <div className="bl">
                  <span>spent this month</span>
                  <span>
                    {m.monthlyCapCents == null
                      ? `${usd(m.spentCents)} · no cap`
                      : `${Math.round((m.spentCents / m.monthlyCapCents) * 100)}%`}
                  </span>
                </div>
                <Meter spentCents={m.spentCents} capCents={m.monthlyCapCents} />
              </div>
            )}
          </div>

          {/* project access */}
          <div className="sheet-sec" ref={accessRef}>
            <div className="sheet-sec-h">
              Project access
              <span className="sec-count">
                {assignedIds ? assignedCount : "·"}/{projects.length}
              </span>
            </div>
            {assignedIds === null ? (
              <p className="sheet-loading">Loading access…</p>
            ) : (
              <>
                <div className="access-list">
                  {projects.map((p) => {
                    const on = assignedIds.includes(p.id);
                    return (
                      <div key={p.id} className="access-row">
                        <ProjectGlyphTile name={p.name} kind={p.kind} />
                        <div className="ar-body">
                          <div className="ar-name">{p.name}</div>
                          <div className="ar-sub">{KIND_LABEL[p.kind] ?? p.kind}</div>
                        </div>
                        <span
                          className={`switch${on ? " on" : ""}`}
                          role="switch"
                          aria-checked={on}
                          aria-label={`${p.name} access`}
                          tabIndex={0}
                          onClick={() => onToggleProject(m.id, p.id, !on)}
                          onKeyDown={(e) => {
                            if (e.key === " " || e.key === "Enter") {
                              e.preventDefault();
                              onToggleProject(m.id, p.id, !on);
                            }
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
                <p className="access-hint">
                  Only add-on projects appear in a member’s sidebar; tools and workspace
                  projects stay reachable by direct link or API.
                </p>
              </>
            )}
          </div>

          {/* activity */}
          <div className="sheet-sec">
            <div className="sheet-sec-h">Activity</div>
            <div className="facts">
              <div className="fact">
                <div className="fk">Status</div>
                <div className="fv">
                  <StatusPill status={m.status} />
                </div>
              </div>
              <div className="fact">
                <div className="fk">Two-factor</div>
                <div className="fv">{m.totpVerified ? "enabled" : "pending"}</div>
              </div>
              <div className="fact">
                <div className="fk">Last active</div>
                <div className="fv">{relativeTime(m.lastLoginAt)}</div>
              </div>
              <div className="fact">
                <div className="fk">Spent · 24h</div>
                <div className="fv">{usd(m.todayCents)}</div>
              </div>
              <div className="fact">
                <div className="fk">Spent · 7d</div>
                <div className="fv">{usd(m.weekCents)}</div>
              </div>
              <div className="fact" style={{ gridColumn: "1 / -1" }}>
                <div className="fk">Membership</div>
                <div className="fv">{joinedLabel(m.createdAt)}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="sheet-foot">
          {m.you ? (
            <button className="btn" onClick={onClose}>
              Done
            </button>
          ) : (
            <button
              className="btn btn-danger"
              onClick={() => onRemove(m.id)}
            >
              <IconTrash style={{ width: 13, height: 13 }} /> Remove from workspace
            </button>
          )}
        </div>
      </aside>
    </>
  );
}
